import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { makeStyles } from 'tss-react/mui'
import {
    errorColor,
    primaryIconSize,
    successColor,
} from '../../lib/styles/universal'
import React, { FC, useCallback, useState } from 'react'
import { GroupViewType, ProfileFormMode } from '../../lib/entities'
import {
    IoEllipsisVertical,
    IoSend,
    IoTrash,
    IoPencil,
    IoArrowRedo,
    IoShuffle,
    IoAddCircle,
    IoAlertCircle,
    IoPerson,
} from 'react-icons/io5'
import { RPUser, colors } from '@hazadapt-git/public-core-base'
import { format } from 'date-fns-tz'
import {
    TeamMemberConfigModal,
    TeamMemberDeleteModal,
    TransferOrgOwnershipModal,
} from '../organisms'
import {
    canUserAddMembers,
    canUserDeleteMembers,
    canUserEditMembers,
    canUserManageMembers,
    isUserActive,
    isUserAdmin,
    isUserAdminOrOwner,
    isUserInvited,
    isUserOwner,
    toast,
} from '../../lib/utils'
import classNames from 'classnames'
import { TruncatedStringWithTooltip } from '../atoms'
import { TeamMemberTypeIcon } from '../molecules'
import { useOrganization } from '../../lib/hooks/useOrganization'

export interface TeamMemberActionsProps {
    activeUser: RPUser
    targetUser: RPUser
    onDeleteClick(): void
    onEditClick(mode: 'edit-member' | 'permissions'): void
    onTransferClick(): void
    inline?: boolean
    open: boolean
    anchorEl: HTMLElement | null
    setAnchorEl(el: HTMLElement | null): void
}

export const TeamMemberActions: FC<TeamMemberActionsProps> = ({
    activeUser,
    targetUser,
    onDeleteClick,
    onEditClick,
    onTransferClick,
    inline,
    open,
    anchorEl,
    setAnchorEl,
}) => {
    const { classes: localClasses } = useLocalStyles()
    const { handleResendInvite } = useOrganization()

    const onOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const onOptionsClose = useCallback(() => {
        setAnchorEl(null)
    }, [setAnchorEl])

    const onResendInviteClick = useCallback(() => {
        handleResendInvite(targetUser)
        setAnchorEl(null)
    }, [handleResendInvite, targetUser, setAnchorEl])

    const handleEditTeamMember = useCallback(() => {
        onEditClick(ProfileFormMode.EDIT_MEMBER)
        setAnchorEl(null)
    }, [onEditClick, setAnchorEl])

    const handleEditPermissions = useCallback(() => {
        onEditClick(ProfileFormMode.PERMISSIONS)
        setAnchorEl(null)
    }, [onEditClick, setAnchorEl])

    const handleDelete = useCallback(() => {
        onDeleteClick()
        setAnchorEl(null)
    }, [onDeleteClick, setAnchorEl])

    const handleTransfer = useCallback(() => {
        onTransferClick()
        setAnchorEl(null)
    }, [onTransferClick, setAnchorEl])

    const isYou = targetUser.rp_user_id === activeUser.rp_user_id
    const activeUserIsAdminOrOwner = isUserAdminOrOwner(activeUser)
    const targetUserIsAdminOrOwner = isUserAdminOrOwner(targetUser)

    const canManageUser =
        // Owner/Admins can manage anyone
        activeUserIsAdminOrOwner ||
        // Non-admins can only manage other non-admins
        (!targetUserIsAdminOrOwner && !activeUserIsAdminOrOwner)

    const showMenu =
        // targetUser is not the activeUser
        !isYou &&
        // activeUser can manage this targetUser (role check)
        canManageUser &&
        // activeUser can manage members generally (permission check)
        canUserManageMembers(activeUser) &&
        // activeUser can add members and targetUser is invited
        ((canUserAddMembers(activeUser) && isUserInvited(targetUser)) ||
            // or activeUser can edit or delete members
            canUserEditMembers(activeUser) ||
            canUserDeleteMembers(activeUser)) &&
        // targetUser is not the owner
        !isUserOwner(targetUser)

    if (!showMenu) return null

    return (
        <div className={classNames({ [localClasses.menuButton]: !inline })}>
            <IconButton onClick={onOptionsClick}>
                <IoEllipsisVertical
                    size={primaryIconSize}
                    color={colors.grays.NOIR}
                    aria-label="Open options menu"
                />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={onOptionsClose}>
                {isUserInvited(targetUser) && canUserAddMembers(activeUser) ? (
                    <MenuItem onClick={onResendInviteClick}>
                        <ListItemIcon>
                            <IoArrowRedo size={primaryIconSize} />
                        </ListItemIcon>
                        <ListItemText>Resend Invitation</ListItemText>
                    </MenuItem>
                ) : null}
                {canUserEditMembers(activeUser) && isUserActive(targetUser) ? (
                    <MenuItem onClick={handleEditTeamMember}>
                        <ListItemIcon>
                            <IoPerson size={primaryIconSize} />
                        </ListItemIcon>
                        <ListItemText>Edit Team Member</ListItemText>
                    </MenuItem>
                ) : null}
                {canUserEditMembers(activeUser) ? (
                    <MenuItem onClick={handleEditPermissions}>
                        <ListItemIcon>
                            <IoPencil size={primaryIconSize} />
                        </ListItemIcon>
                        <ListItemText>Edit Permissions</ListItemText>
                    </MenuItem>
                ) : null}
                {/* TODO: Pause Access functionality */}
                {/* <MenuItem>
                    <ListItemIcon>
                        <IoPause size={primaryIconSize} />
                    </ListItemIcon>
                    <ListItemText>Pause Access</ListItemText>
                </MenuItem> */}
                {isUserOwner(activeUser) && isUserAdmin(targetUser) ? (
                    <MenuItem onClick={handleTransfer}>
                        <ListItemIcon>
                            <IoShuffle size={primaryIconSize} />
                        </ListItemIcon>
                        <ListItemText>Transfer Ownership</ListItemText>
                    </MenuItem>
                ) : null}
                {canUserDeleteMembers(activeUser) ? (
                    <MenuItem onClick={handleDelete}>
                        <ListItemIcon>
                            <IoTrash
                                size={primaryIconSize}
                                color={colors.primary.CORAL}
                            />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography
                                color={colors.primary.CORAL}
                                fontWeight={500}
                            >
                                Remove from Organization
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                ) : null}
            </Menu>
        </div>
    )
}
export interface TeamMemberProps {
    targetUser: RPUser
    groupView: GroupViewType
    activeUser: RPUser
}

export const TeamMemberView: FC<TeamMemberProps> = ({
    targetUser,
    groupView,
    activeUser,
}) => {
    const [configModalOpen, setConfigModalOpen] = useState<boolean>(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
    const [transferModalOpen, setTransferModalOpen] = useState<boolean>(false)
    const [configModalMode, setConfigModalMode] = useState<
        ProfileFormMode.EDIT_MEMBER | ProfileFormMode.PERMISSIONS
    >(ProfileFormMode.EDIT_MEMBER)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const { classes: localClasses } = useLocalStyles()

    const onEditClick = (
        mode: ProfileFormMode.EDIT_MEMBER | ProfileFormMode.PERMISSIONS
    ) => {
        setConfigModalMode(mode)
        setConfigModalOpen(true)
    }

    const copyEmailToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(targetUser.email)
            toast(
                'Email address copied to clipboard!',
                <IoAddCircle color={successColor} size={primaryIconSize} />
            )
        } catch (err) {
            console.error('Failed to copy email:', err)
            toast(
                'Failed to copy email address to clipboard.',
                <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                'error'
            )
        }
    }

    return (
        <>
            {groupView === 'card' ? (
                // card view
                <div className={localClasses.card}>
                    <Box
                        className={classNames(
                            localClasses.box,
                            isUserInvited(targetUser)
                                ? localClasses.invitedUser
                                : undefined
                        )}
                    >
                        <Badge
                            overlap="circular"
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            badgeContent={
                                isUserInvited(targetUser) ? (
                                    <IoSend
                                        size="1.25rem"
                                        color={colors.grays.BLANC}
                                        className={localClasses.invitedBadge}
                                    />
                                ) : null
                            }
                            sx={{
                                margin: '0.5rem',
                                height: 'fit-content',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: {
                                        xs: '3.5rem',
                                        md: '4.75rem',
                                        lg: '6.75rem',
                                    },
                                    height: {
                                        xs: '3.5rem',
                                        md: '4.75rem',
                                        lg: '6.75rem',
                                    },
                                }}
                                src={
                                    targetUser.profile_picture_uri ?? undefined
                                }
                                alt={
                                    targetUser.profile_picture_uri
                                        ? `${targetUser.first_name}'s profile picture`
                                        : `Default profile picture for ${targetUser.first_name} ${targetUser.last_name}`
                                }
                            />
                        </Badge>
                        <Typography
                            variant="h4"
                            fontWeight={500}
                            textAlign="center"
                            display="flex"
                            alignItems="center"
                            sx={{
                                wordWrap: 'break-word',
                            }}
                        >
                            {`${targetUser.first_name} ${targetUser.last_name}`}
                            {!isUserInvited(targetUser) &&
                            isUserAdminOrOwner(targetUser) ? (
                                <TeamMemberTypeIcon
                                    user={targetUser}
                                    iconClassName={
                                        localClasses.ownerOrAdminBadge
                                    }
                                />
                            ) : null}
                        </Typography>
                        <Typography
                            fontWeight={400}
                            textAlign="center"
                            mt={'-.75rem'}
                        >
                            {isUserInvited(targetUser)
                                ? 'Invited'
                                : targetUser.title}
                        </Typography>
                        <TruncatedStringWithTooltip
                            onClick={copyEmailToClipboard}
                            styles={{
                                cursor: 'copy',
                                textAlign: 'center',
                                fontStyle: 'italic',
                            }}
                        >
                            {targetUser.email}
                        </TruncatedStringWithTooltip>
                        <TeamMemberActions
                            activeUser={activeUser}
                            targetUser={targetUser}
                            onDeleteClick={() => setDeleteModalOpen(true)}
                            onEditClick={onEditClick}
                            onTransferClick={() => setTransferModalOpen(true)}
                            open={open}
                            anchorEl={anchorEl}
                            setAnchorEl={setAnchorEl}
                        />
                    </Box>
                </div>
            ) : (
                // list view
                <TableRow
                    sx={{ '& > *': { borderBottom: 'unset', width: '25%' } }}
                >
                    <TableCell>
                        <Typography
                            textAlign="left"
                            fontWeight={500}
                            alignItems={'center'}
                            display="flex"
                        >
                            {`${targetUser.first_name} ${targetUser.last_name}`}
                            {!isUserInvited(targetUser) &&
                            isUserAdminOrOwner(targetUser) ? (
                                <TeamMemberTypeIcon
                                    user={targetUser}
                                    iconClassName={
                                        localClasses.ownerOrAdminBadge
                                    }
                                />
                            ) : null}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography
                            fontStyle={
                                isUserInvited(targetUser) ? 'italic' : 'normal'
                            }
                        >
                            {isUserInvited(targetUser)
                                ? 'Invited'
                                : targetUser.title}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography
                            onClick={copyEmailToClipboard}
                            fontStyle={'italic'}
                            sx={{
                                wordBreak: 'break-word',
                                cursor: 'copy',
                            }}
                        >
                            {targetUser.email}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <div className={localClasses.listViewDateAndActions}>
                            {targetUser.date_created && (
                                <Typography>
                                    {format(
                                        new Date(targetUser.date_created),
                                        'MMMM d, y'
                                    )}
                                </Typography>
                            )}
                            <TeamMemberActions
                                activeUser={activeUser}
                                targetUser={targetUser}
                                onDeleteClick={() => setDeleteModalOpen(true)}
                                onEditClick={onEditClick}
                                onTransferClick={() =>
                                    setTransferModalOpen(true)
                                }
                                inline
                                open={open}
                                anchorEl={anchorEl}
                                setAnchorEl={setAnchorEl}
                            />
                        </div>
                    </TableCell>
                </TableRow>
            )}
            <TeamMemberConfigModal
                targetUser={targetUser}
                activeUser={activeUser}
                open={configModalOpen}
                onClose={() => setConfigModalOpen(false)}
                mode={configModalMode}
            />
            <TeamMemberDeleteModal
                user={targetUser}
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            />
            <TransferOrgOwnershipModal
                user={targetUser}
                open={transferModalOpen}
                onClose={() => setTransferModalOpen(false)}
            />
        </>
    )
}

const useLocalStyles = makeStyles()({
    box: {
        width: '100%',
        display: 'grid',
        gridTemplateRows: '1fr',
        padding: '1rem',
        boxShadow: '0rem 0.25rem 0.25rem rgba(0, 0, 0, 0.1)',
        borderRadius: '1rem',
        justifyItems: 'center',
        gap: '1rem',
    },
    invitedUser: {
        backgroundColor: colors.grays.WHITESMOKE,
    },
    card: {
        display: 'flex',
        justifyContent: 'space-between',
        height: '100%',
        position: 'relative',
    },
    icon: {
        width: '2.75rem',
        height: '2.75rem',
        objectFit: 'contain',
    },
    list: {
        padding: '1rem 2rem',
        flexGrow: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        alignItems: 'center',
    },
    invitedBadge: {
        padding: '0.25rem',
        backgroundColor: colors.grays.THUNDERCLOUD,
        borderRadius: '25%',
    },
    ownerOrAdminBadge: {
        flexShrink: 0,
        display: 'inline-block',
        verticalAlign: 'middle',
        marginLeft: '0.5rem',
    },
    menuButton: {
        position: 'absolute',
        top: '0.5rem',
        right: '0.5rem',
    },
    listViewDateAndActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
    },
})
